import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ENGINE_LABELS, type Engine } from "@velivo/shared";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";
import { DataTable, type Column } from "../components/DataTable";
import { Tabs } from "../components/Tabs";
import { SidePanel } from "../components/SidePanel";
import { Icon } from "../components/Icon";
import { useNavigate } from "react-router-dom";
import "./groundtruth.css";
import "./drift.css";
import "./dashboard.css";

/**
 * PRODUCT_FLOW.md Stage 5 §4 — drill-down to evidence.
 *
 * "This is the step that makes the score trustworthy — a number with no path to
 * raw evidence gets ignored by a skeptical CCO." So the table is the triage
 * list and the panel is the proof: response text, the exact citation, its
 * authority tier, and why the root cause is what it says (FR-PROV-01/02/03).
 */

type Severity = "aligned" | "erosion" | "critical";
type DriftType =
  "omission" | "distortion" | "outdated_fact" | "hostile_context";
type RootCause = "knowledge_gap" | "contamination" | "undetermined";
type AuthorityTier =
  | "tier1_media"
  | "regulatory_filing"
  | "corporate_owned"
  | "blog_forum"
  | "unknown";

interface FlagRow {
  flagId: string;
  pillarId: string;
  engine: Engine;
  driftType: DriftType;
  severity: Severity;
  rootCause: RootCause;
  rootCauseRationale: string | null;
  description: string;
  groundTruthStatement: string | null;
  impactScore: number;
  createdAt: string;
}

interface CitationDetail {
  citationId: string;
  url: string | null;
  sourceName: string;
  snippet: string | null;
  authorityTier: AuthorityTier;
  authorityTierLabel: string;
  tierAssignedBy: "auto" | "manual_override";
  isDriving: boolean;
}

interface FlagDetail extends Omit<FlagRow, "pillarId" | "createdAt"> {
  provenanceAvailable: boolean;
  drivingCitationId: string | null;
  citations: CitationDetail[];
}

interface SourceReport {
  totalCitations: number;
  byTier: {
    tier: AuthorityTier;
    label: string;
    count: number;
    share: number;
  }[];
  topSources: {
    host: string;
    sourceName: string;
    tier: AuthorityTier;
    label: string;
    citations: number;
    drivingFlags: number;
  }[];
}

const DRIFT_LABELS: Record<DriftType, string> = {
  omission: "Omission",
  distortion: "Distortion",
  outdated_fact: "Outdated fact",
  hostile_context: "Hostile context",
};

const ROOT_CAUSE_LABELS: Record<RootCause, string> = {
  knowledge_gap: "Knowledge gap",
  contamination: "Contamination",
  undetermined: "Undetermined",
};

const TIER_OPTIONS: { value: AuthorityTier; label: string }[] = [
  { value: "tier1_media", label: "Tier 1 media" },
  { value: "regulatory_filing", label: "Regulatory filing" },
  { value: "corporate_owned", label: "Corporate owned" },
  { value: "blog_forum", label: "Blog / forum" },
  { value: "unknown", label: "Unclassified" },
];

const SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  erosion: 1,
  aligned: 2,
};

export function DriftPage() {
  const { activeBrand } = useAuth();
  const activeBrandId = activeBrand?.brandId ?? null;
  const [flags, setFlags] = useState<FlagRow[] | null>(null);
  const [pillars, setPillars] = useState<{ pillarId: string; name: string }[]>(
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const [openFlagId, setOpenFlagId] = useState<string | null>(null);

  // The dashboard deep-links here with the slice the user clicked (FR-DASH-01
  // and FR-DASH-02's ACs). Arriving at an unfiltered list of every flag would
  // make the drill-down a navigation step rather than an answer.
  const [params, setParams] = useSearchParams();
  const focusPillarId = params.get("pillarId") ?? "";
  const focusEngine = params.get("engine") ?? "";
  const focusSeverity = params.get("severity") ?? "";

  // A radar card links straight to its own evidence; opening the list and
  // making the user find the row again would waste the click.
  const deepLinkedFlagId = params.get("flagId");
  useEffect(() => {
    if (deepLinkedFlagId) setOpenFlagId(deepLinkedFlagId);
  }, [deepLinkedFlagId]);

  const loadFlags = useCallback(async () => {
    if (!activeBrandId) return;
    try {
      const data = await api<{ items: FlagRow[] }>(
        `/brands/${activeBrandId}/drift-flags?limit=200`,
      );
      // Severity-ranked by default (FR-DASH-04) — a triage list that opens on
      // "oldest first" makes the operator do the sorting the product exists for.
      setFlags(
        [...data.items].sort(
          (a, b) =>
            SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
            b.impactScore - a.impactScore,
        ),
      );
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not load drift flags.",
      );
      setFlags([]);
    }
  }, [activeBrandId]);

  useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  useEffect(() => {
    if (!activeBrandId) return;
    void api<{ items: { pillarId: string; name: string }[] }>(
      `/brands/${activeBrandId}/pillars`,
    )
      .then((data) => setPillars(data.items))
      .catch(() => setPillars([]));
  }, [activeBrandId]);

  const visibleFlags = useMemo(() => {
    if (!flags) return null;
    return flags.filter(
      (f) =>
        (!focusPillarId || f.pillarId === focusPillarId) &&
        (!focusEngine || f.engine === focusEngine) &&
        (!focusSeverity || f.severity === focusSeverity),
    );
  }, [flags, focusPillarId, focusEngine, focusSeverity]);

  const focusCount = [focusPillarId, focusEngine, focusSeverity].filter(
    Boolean,
  ).length;

  const pillarName = useMemo(() => {
    const map = new Map(pillars.map((p) => [p.pillarId, p.name]));
    return (id: string) => map.get(id) ?? "—";
  }, [pillars]);

  const columns: Column<FlagRow>[] = [
    {
      key: "severity",
      header: "Severity",
      width: "116px",
      value: (r) => SEVERITY_ORDER[r.severity],
      render: (r) => (
        <span className={`badge badge--${r.severity}`}>{r.severity}</span>
      ),
    },
    {
      key: "driftType",
      header: "Type",
      width: "132px",
      value: (r) => DRIFT_LABELS[r.driftType],
      render: (r) => (
        <span className="badge badge--neutral">
          {DRIFT_LABELS[r.driftType]}
        </span>
      ),
    },
    {
      key: "pillar",
      header: "Narrative Pillar",
      value: (r) => pillarName(r.pillarId),
      render: (r) => pillarName(r.pillarId),
    },
    {
      key: "engine",
      header: "Engine",
      width: "128px",
      value: (r) => ENGINE_LABELS[r.engine] ?? r.engine,
      render: (r) => ENGINE_LABELS[r.engine] ?? r.engine,
    },
    {
      key: "rootCause",
      header: "Root cause",
      width: "150px",
      value: (r) => ROOT_CAUSE_LABELS[r.rootCause],
      render: (r) => (
        <span className={`badge badge--cause-${r.rootCause}`}>
          {ROOT_CAUSE_LABELS[r.rootCause]}
        </span>
      ),
    },
    {
      key: "description",
      header: "What the engine said",
      value: (r) => r.description,
      render: (r) => <span className="drift__desc">{r.description}</span>,
    },
    {
      key: "impact",
      header: "Impact",
      width: "84px",
      align: "right",
      value: (r) => r.impactScore,
      render: (r) => <span className="numeric">{r.impactScore}</span>,
    },
    {
      key: "evidence",
      header: "",
      width: "116px",
      render: (r) => (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setOpenFlagId(r.flagId)}
        >
          Evidence <Icon name="arrowRight" />
        </button>
      ),
    },
  ];

  return (
    <div className="stack">
      <header className="page__header">
        <div>
          <h1 className="page__title">Narrative drift</h1>
          <p className="page__subtitle">
            Every flagged discrepancy, ranked by severity, with the exact source
            behind it.
          </p>
        </div>
      </header>

      {error ? <div className="alert alert--error">{error}</div> : null}

      {focusCount > 0 ? (
        <div className="filterbar">
          <span className="filterbar__group">
            Showing{" "}
            {focusSeverity ? (
              <span className={`badge badge--${focusSeverity}`}>
                {focusSeverity}
              </span>
            ) : null}
            {focusPillarId ? (
              <span className="badge badge--neutral">
                {pillarName(focusPillarId)}
              </span>
            ) : null}
            {focusEngine ? (
              <span className="badge badge--neutral">
                {ENGINE_LABELS[focusEngine as Engine] ?? focusEngine}
              </span>
            ) : null}
          </span>
          <span className="filterbar__spacer" />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
          >
            Clear
          </button>
        </div>
      ) : null}

      <Tabs
        ariaLabel="Drift sections"
        tabs={[
          {
            id: "flags",
            label: "Drift flags",
            icon: "drift",
            badge:
              visibleFlags?.filter((f) => f.severity === "critical").length ||
              null,
            render: () => (
              <DataTable
                rows={visibleFlags}
                columns={columns}
                rowKey={(r) => r.flagId}
                searchPlaceholder="Search by pillar, engine, or what the engine said…"
                empty={{
                  icon: "check",
                  title:
                    focusCount > 0
                      ? "Nothing flagged in this slice"
                      : "No drift flagged yet",
                  description:
                    focusCount > 0
                      ? "No drift matches the filters you arrived with. Clear them to see everything flagged for this Brand."
                      : "Once a capture run is scored, anything that diverges from your approved Ground Truth appears here — worst first.",
                  action:
                    focusCount > 0 ? (
                      <button
                        type="button"
                        className="btn btn--secondary"
                        onClick={() =>
                          setParams(new URLSearchParams(), { replace: true })
                        }
                      >
                        Clear filters
                      </button>
                    ) : undefined,
                }}
              />
            ),
          },
          {
            id: "sources",
            label: "Source authority",
            icon: "link",
            render: () => <SourcesTab pillars={pillars} />,
          },
        ]}
      />

      {openFlagId ? (
        <FlagEvidencePanel
          flagId={openFlagId}
          onClose={() => setOpenFlagId(null)}
          onChanged={loadFlags}
        />
      ) : null}
    </div>
  );
}

interface Recommendation {
  category:
    | "media_outreach"
    | "owned_schema_fix"
    | "publisher_correction"
    | "escalation";
  title: string;
  rationale: string;
  targetSource: string | null;
  draftTypes: string[];
}

const CATEGORY_LABELS: Record<Recommendation["category"], string> = {
  media_outreach: "Earned media outreach",
  owned_schema_fix: "Owned data & schema fix",
  publisher_correction: "Publisher correction",
  escalation: "Escalation",
};

function FlagEvidencePanel({
  flagId,
  onClose,
  onChanged,
}: {
  flagId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const mayAct = can("brand:manage_action_items");
  const [recommendations, setRecommendations] = useState<
    Recommendation[] | null
  >(null);
  const [creating, setCreating] = useState<string | null>(null);
  const [detail, setDetail] = useState<FlagDetail | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      // Both, together. A recommendation is derived from the citation's
      // authority tier, so reloading the flag without reloading the
      // recommendation left the panel showing a "Blog / forum" citation
      // alongside advice that still described it as Unclassified.
      const [flag, recs] = await Promise.all([
        api<FlagDetail>(`/drift-flags/${flagId}`),
        api<{ recommendations: Recommendation[] }>(
          `/drift-flags/${flagId}/recommendations`,
        ),
      ]);
      setDetail(flag);
      setRecommendations(recs.recommendations);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not load this flag.",
      );
    }
  }, [flagId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function takeAction(rec: Recommendation) {
    setCreating(rec.title);
    setError(null);
    try {
      const created = await api<{ actionItemId: string }>(
        `/drift-flags/${flagId}/action-items`,
        {
          method: "POST",
          body: {
            category: rec.category,
            title: rec.title,
            rationale: rec.rationale,
          },
        },
      );
      onChanged();
      navigate(`/actions?actionItemId=${created.actionItemId}`);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not create the action.",
      );
    } finally {
      setCreating(null);
    }
  }

  async function overrideTier(
    citationId: string,
    authorityTier: AuthorityTier,
  ) {
    setBusy(citationId);
    setError(null);
    try {
      await api(`/citations/${citationId}`, {
        method: "PATCH",
        body: { authorityTier },
      });
      // Re-run provenance so the root-cause narrative reflects the new tier —
      // otherwise the override changes a label but not the reasoning built on it.
      await api(`/drift-flags/${flagId}/reclassify`, {
        method: "POST",
        body: {},
      });
      await load();
      onChanged();
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Could not update the tier.",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <SidePanel
      open
      title={detail ? DRIFT_LABELS[detail.driftType] : "Evidence"}
      subtitle={
        detail ? (
          <>
            <span className={`badge badge--${detail.severity}`}>
              {detail.severity}
            </span>
            <span className={`badge badge--cause-${detail.rootCause}`}>
              {ROOT_CAUSE_LABELS[detail.rootCause]}
            </span>
            <span>{ENGINE_LABELS[detail.engine] ?? detail.engine}</span>
          </>
        ) : null
      }
      onClose={onClose}
    >
      {error ? <div className="alert alert--error">{error}</div> : null}
      {!detail ? (
        <div className="skeleton-stack">
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton skeleton--short" />
        </div>
      ) : (
        // Three tabs rather than five stacked sections. The order follows how a
        // Comms Manager actually works a finding: read what happened, check the
        // evidence, then decide what to do — and each step is short enough to
        // read without scrolling past the one before it.
        //
        // urlSync is off: the page underneath already owns the URL hash for its
        // own tabs, and two instances writing to it would switch the page's tab
        // behind the panel.
        <Tabs
          urlSync={false}
          compact
          ariaLabel="Finding detail"
          tabs={[
            {
              id: "finding",
              label: "The finding",
              icon: "drift",
              render: () => (
                <>
                  <section className="evidence">
                    <h3 className="evidence__label">What the engine said</h3>
                    <p className="evidence__quote evidence__quote--drift">
                      {detail.description}
                    </p>
                  </section>

                  {detail.groundTruthStatement ? (
                    <section className="evidence">
                      <h3 className="evidence__label">
                        What your Ground Truth says
                      </h3>
                      <p className="evidence__quote evidence__quote--truth">
                        {detail.groundTruthStatement}
                      </p>
                    </section>
                  ) : null}

                  <section className="evidence">
                    <h3 className="evidence__label">
                      Why this is{" "}
                      {ROOT_CAUSE_LABELS[detail.rootCause].toLowerCase()}
                    </h3>
                    {/* FR-PROV-03's AC: show the rationale, not just the label. */}
                    <p className="evidence__rationale">
                      {detail.rootCauseRationale ??
                        "No rationale recorded for this flag."}
                    </p>
                  </section>
                </>
              ),
            },
            {
              id: "sources",
              label: "Sources",
              icon: "link",
              badge: detail.citations.length || null,
              render: () => (
                <section className="evidence">
                  {!detail.provenanceAvailable ? (
                    // FR-PROV-01's AC — say so rather than showing an empty box
                    // that reads as "we didn't look".
                    <p className="evidence__none">
                      <Icon name="alert" /> No provenance available — this
                      engine exposed no citations for this answer, so the source
                      behind it can&apos;t be identified.
                    </p>
                  ) : (
                    <ul className="citations">
                      {detail.citations.map((c) => (
                        <li
                          key={c.citationId}
                          className={`citation${c.isDriving ? " citation--driving" : ""}`}
                        >
                          <div className="citation__head">
                            <span
                              className={`badge badge--tier-${c.authorityTier}`}
                            >
                              {c.authorityTierLabel}
                            </span>
                            {c.isDriving ? (
                              <span className="badge badge--critical">
                                Driving source
                              </span>
                            ) : null}
                            {c.tierAssignedBy === "manual_override" ? (
                              <span className="badge badge--neutral">
                                Manually classified
                              </span>
                            ) : null}
                          </div>
                          <div className="citation__name">
                            {c.sourceName || "Unnamed source"}
                          </div>
                          {c.url ? (
                            <a
                              className="citation__url"
                              href={c.url}
                              target="_blank"
                              rel="noreferrer noopener"
                            >
                              <Icon name="link" /> {c.url}
                            </a>
                          ) : null}
                          {c.snippet ? (
                            <p className="citation__snippet">{c.snippet}</p>
                          ) : null}
                          <label className="citation__override">
                            <span>Authority tier</span>
                            <select
                              value={c.authorityTier}
                              disabled={busy === c.citationId}
                              onChange={(e) =>
                                void overrideTier(
                                  c.citationId,
                                  e.target.value as AuthorityTier,
                                )
                              }
                            >
                              {TIER_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ),
            },
            {
              id: "action",
              label: "Action",
              icon: "actions",
              badge: recommendations?.length || null,
              render: () => (
                <section className="evidence">
                  {recommendations === null ? (
                    <div className="skeleton" style={{ height: 72 }} />
                  ) : recommendations.length === 0 ? (
                    <p className="muted">
                      No recommendation could be derived for this finding.
                    </p>
                  ) : (
                    <ul className="recs">
                      {recommendations.map((rec) => (
                        <li className="rec" key={rec.title}>
                          <div className="rec__head">
                            <span className="badge badge--accent">
                              {CATEGORY_LABELS[rec.category]}
                            </span>
                            <strong>{rec.title}</strong>
                          </div>
                          {/* FR-ACT-01's AC — the rationale names the actual
                              evidence, so the user can judge the advice rather
                              than trust it. */}
                          <p className="rec__why">{rec.rationale}</p>
                          {mayAct ? (
                            <button
                              type="button"
                              className="btn btn--primary btn--sm"
                              disabled={creating !== null}
                              onClick={() => void takeAction(rec)}
                            >
                              {creating === rec.title
                                ? "Creating…"
                                : "Take this action"}
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ),
            },
          ]}
        />
      )}
    </SidePanel>
  );
}

function SourcesTab({
  pillars,
}: {
  pillars: { pillarId: string; name: string }[];
}) {
  const [pillarId, setPillarId] = useState("");
  const [report, setReport] = useState<SourceReport | null>(null);

  useEffect(() => {
    if (!pillarId && pillars[0]) setPillarId(pillars[0].pillarId);
  }, [pillars, pillarId]);

  useEffect(() => {
    if (!pillarId) return;
    setReport(null);
    void api<SourceReport>(`/pillars/${pillarId}/citation-sources`)
      .then(setReport)
      .catch(() =>
        setReport({ totalCitations: 0, byTier: [], topSources: [] }),
      );
  }, [pillarId]);

  return (
    <div className="stack">
      <label className="inline-select">
        <span>Narrative Pillar</span>
        <select value={pillarId} onChange={(e) => setPillarId(e.target.value)}>
          {pillars.map((p) => (
            <option key={p.pillarId} value={p.pillarId}>
              {p.name}
            </option>
          ))}
        </select>
      </label>

      {report && report.byTier.length > 0 ? (
        <div
          className="tierbar"
          role="img"
          aria-label="Share of citations by authority tier"
        >
          {report.byTier.map((t) => (
            <div
              key={t.tier}
              className={`tierbar__seg tierbar__seg--${t.tier}`}
              style={{ width: `${t.share}%` }}
              title={`${t.label}: ${t.count} (${t.share}%)`}
            >
              {t.share >= 12 ? `${t.label} ${t.share}%` : null}
            </div>
          ))}
        </div>
      ) : null}

      <DataTable
        rows={report?.topSources ?? null}
        rowKey={(r) => r.host}
        searchPlaceholder="Search sources…"
        columns={[
          {
            key: "host",
            header: "Source",
            value: (r) => `${r.sourceName} ${r.host}`,
            render: (r) => (
              <>
                <div className="citation__name">{r.sourceName}</div>
                <div className="muted">{r.host}</div>
              </>
            ),
          },
          {
            key: "tier",
            header: "Authority tier",
            width: "160px",
            value: (r) => r.label,
            render: (r) => (
              <span className={`badge badge--tier-${r.tier}`}>{r.label}</span>
            ),
          },
          {
            key: "citations",
            header: "Citations",
            width: "104px",
            align: "right",
            value: (r) => r.citations,
            render: (r) => <span className="numeric">{r.citations}</span>,
          },
          {
            key: "driving",
            header: "Flags driven",
            width: "120px",
            align: "right",
            value: (r) => r.drivingFlags,
            render: (r) => (
              <span
                className={`numeric${r.drivingFlags > 0 ? " numeric--critical" : ""}`}
              >
                {r.drivingFlags}
              </span>
            ),
          },
        ]}
        empty={{
          icon: "link",
          title: "No citations captured yet for this pillar",
          description:
            "Sources appear here once a scored run returns answers that cite something. Engines that expose no citations are reported as “no provenance available” on the flag itself.",
        }}
      />
    </div>
  );
}
